import { useTranslation } from "react-i18next";

/*
 * ElectricalSafetyEditModal 역할:
 * - 전기안전 보고서에서 수정 가능한 메타데이터와 점검 결과를 모달 안에서 편집한다.
 * - 보고서 본문과 같은 필드 이름을 유지하면서 저장 / 취소 액션만 분리해 제공한다.
 */
export default function ElectricalSafetyEditModal({
  file,
  inspectorDate,
  handleChange,
  onCancel,
  onSave,
  isSaving,
  hasChanges,
}) {
  const { t } = useTranslation();

  return (
    <div
      className="reportEditModalOverlay"
      role="presentation"
      onClick={onCancel}
    >
      <div
        className="reportEditModal electricalEditModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="electrical-report-edit-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="reportEditModalHeader">
          <div className="titleBox">
            <h3 id="electrical-report-edit-title">
              {t("imageFileIndex.electricalSafety.editModal.title", {
                defaultValue: "Electrical Report Edit",
              })}
            </h3>
            <p>
              {t("imageFileIndex.electricalSafety.editModal.description", {
                defaultValue:
                  "Adjust the editable report metadata and save directly here.",
              })}
            </p>
          </div>

          <button
            type="button"
            className="iconBtn"
            onClick={onCancel}
            aria-label={t("imageFileIndex.electricalSafety.editModal.closeAria", {
              defaultValue: "Close edit modal",
            })}
          >
            X
          </button>
        </div>

        <div className="reportEditModalBody">
          <div className="reportEditGrid">
            <label className="reportEditField">
              <span className="label">
                {t("imageFileIndex.electricalSafety.editModal.fields.measuredBy", {
                  defaultValue: "Measured By",
                })}
              </span>
              <input
                id="measured_by"
                type="text"
                value={file?.measured_by ?? ""}
                onChange={handleChange}
              />
            </label>

            <label className="reportEditField">
              <span className="label">
                {t(
                  "imageFileIndex.electricalSafety.editModal.fields.facilityName",
                  {
                    defaultValue: "Facility Name",
                  },
                )}
              </span>
              <input
                id="facility_name"
                type="text"
                value={file?.facility_name ?? ""}
                onChange={handleChange}
              />
            </label>

            <label className="reportEditField">
              <span className="label">
                {t(
                  "imageFileIndex.electricalSafety.editModal.fields.operatingVoltage",
                  {
                    defaultValue: "Operating Voltage",
                  },
                )}
              </span>
              <input
                id="operating_voltage"
                type="text"
                value={file?.operating_voltage ?? ""}
                onChange={handleChange}
              />
            </label>
          </div>

          <div className="reportEditGrid">
            <div className="reportEditField isReadonlyField">
              <span className="label">
                {t("imageFileIndex.electricalSafety.editModal.fields.measuredDate", {
                  defaultValue: "Measured Date",
                })}
              </span>
              <div className="readonlyValue">
                {[inspectorDate.year, inspectorDate.month, inspectorDate.day]
                  .filter(Boolean)
                  .join(" / ") || "-"}
              </div>
            </div>
          </div>

          <label className="reportEditField">
            <span className="label">
              {t(
                "imageFileIndex.electricalSafety.editModal.fields.measurementConditions",
                {
                  defaultValue: "Measurement Conditions",
                },
              )}
            </span>
            <input
              id="measurement_conditions"
              type="text"
              value={file?.measurement_conditions ?? ""}
              onChange={handleChange}
            />
          </label>

          <label className="reportEditField textareaField">
            <span className="label">
              {t(
                "imageFileIndex.electricalSafety.editModal.fields.inspectionResult",
                {
                  defaultValue: "Inspection Result",
                },
              )}
            </span>
            <textarea
              id="inspection_result"
              value={file?.inspection_result ?? ""}
              onChange={handleChange}
            />
          </label>
        </div>

        <div className="reportEditModalFooter">
          <button
            type="button"
            className="ghostBtn"
            onClick={onCancel}
            disabled={isSaving}
          >
            {t("editorBtnBox.cancelButton")}
          </button>
          <button
            type="button"
            className="primaryBtn"
            onClick={onSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving
              ? t("imageFileIndex.electricalSafety.editModal.saving", {
                  defaultValue: "Saving...",
                })
              : t("editorBtnBox.saveButton")}
          </button>
        </div>
      </div>
    </div>
  );
}
